import React, { useState } from 'react';

import {
  ConsumptionForecastOverview,
  ForecastOverviewUnit,
} from 'entities/ConsumptionForecastOverview';

import { getConsumptionForecastOverviewService } from 'services/consumer_unit.service';

import { useAppSelector } from 'store/hooks';
import { selectConsumerUnitList } from 'store/consumer_unit/consumer_unit.selector';

import {
  Card,
  CardTitle,
  TrendBadge,
  Table,
  TableScroll,
  Note,
  Empty,
} from 'components/consumption-analysis/consumption-analysis.styles';

import { format } from 'utils/format';
import { TermInfo } from 'components/term-info/term-info.component';

import {
  Container,
  ForecastButton,
  UnitList,
  UnitRow,
  UnitName,
  TotalLine,
} from './consumption-forecast.styles';

const FORECAST_NOTE =
  'Valores previstos com a bandeira e a tarifa ANEEL vigentes. A previsão usa o histórico de faturas (realizado) e já desconta a geração esperada das usinas, os créditos recebidos pelo rateio e o saldo de créditos; mudanças de consumo podem alterar o resultado.';

/** Gasto previsto da UC no mês (null = sem previsão ou sem tarifa PCAT). */
const unitCost = (unit: ForecastOverviewUnit, refMonth: string) =>
  unit.forecast.find((m) => m.refMonth === refMonth)?.cost ?? null;

const TREND_LABEL: Record<string, string> = {
  rising: 'Tendência de alta',
  falling: 'Tendência de queda',
  stable: 'Estável',
};

/**
 * Card do dashboard do cliente: tendência e previsão de gasto do mês seguinte e
 * dos 3 meses, somando TODAS as UCs da conta. Consome
 * `GET /consumer-units/consumption-forecast` (reusa a mesma análise da tela da
 * UC — série da FATURA, R$ por PCAT via motor).
 *
 * Carrega SOB DEMANDA (botão): o endpoint roda ~3 `computeAudit` por UC, então
 * não deve rodar no boot do dashboard. Nada vai para o Redux — o R$ previsto
 * muda com bandeira/tarifa vigentes e não deve ficar cacheado.
 */
export const ConsumptionForecastCard = () => {
  const consumerUnitList = useAppSelector(selectConsumerUnitList);
  const [overview, setOverview] = useState<ConsumptionForecastOverview | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (consumerUnitList.length === 0) return null;

  const handleLoad = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getConsumptionForecastOverviewService();
      setOverview(response.data.data);
    } catch {
      setError('Não foi possível calcular a previsão agora. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const hasForecast = !!overview?.months.length;

  return (
    <Container>
      <Card>
        <CardTitle>
          <span>
            Tendência e previsão de gasto
            <TermInfo text={FORECAST_NOTE} />
          </span>
        </CardTitle>

        {!overview && (
          <>
            <Empty>
              Projetamos o consumo futuro pela sua série de faturas e o gasto
              com as tarifas oficiais da ANEEL (PCAT) vigentes — para todas as
              unidades da conta.
            </Empty>
            <ForecastButton
              type="button"
              disabled={loading}
              onClick={handleLoad}
            >
              {loading ? 'Calculando...' : 'Calcular tendência e previsão'}
            </ForecastButton>
          </>
        )}

        {error && <Empty>{error}</Empty>}

        {overview && !hasForecast && (
          <Empty>
            Ainda não há faturas suficientes para projetar (mínimo de 12 meses).
            Envie as faturas anteriores das suas unidades para liberar a
            previsão.
          </Empty>
        )}

        {overview && hasForecast && (
          <>
            <UnitList>
              {overview.units.map((unit) => (
                <UnitRow key={unit.consumerUnitId}>
                  <UnitName>{unit.name}</UnitName>
                  {unit.trend ? (
                    <TrendBadge $direction={unit.trend.direction}>
                      {TREND_LABEL[unit.trend.direction]} ·{' '}
                      {format.number(Math.abs(unit.trend.pctPerMonth), 1)}%/mês
                    </TrendBadge>
                  ) : (
                    <UnitName style={{ fontWeight: 400, color: '#999' }}>
                      Sem dados suficientes
                    </UnitName>
                  )}
                </UnitRow>
              ))}
            </UnitList>

            {/* Gasto previsto por imóvel × mês (30/out/2026, pedido do Bruno —
                antes a tabela tinha só o total da conta, com o consumo). O
                total do imóvel só aparece se todos os meses tiverem valor. */}
            <TableScroll>
              <Table>
                <thead>
                  <tr>
                    <th>Imóvel</th>
                    {overview.months.map((month) => (
                      <th key={month.refMonth}>
                        {format.refMonth(month.refMonth)}
                      </th>
                    ))}
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.units.map((unit) => {
                    const costs = overview.months.map((month) =>
                      unitCost(unit, month.refMonth)
                    );
                    const complete = costs.every((cost) => cost != null);
                    const total = costs.reduce<number>(
                      (sum, cost) => sum + (cost ?? 0),
                      0
                    );
                    return (
                      <tr key={unit.consumerUnitId}>
                        <td>{unit.name}</td>
                        {costs.map((cost, i) => (
                          <td
                            key={overview.months[i].refMonth}
                            className="money"
                          >
                            {cost != null ? `R$ ${format.money(cost)}` : '—'}
                          </td>
                        ))}
                        <td className="money">
                          {complete ? `R$ ${format.money(total)}` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableScroll>

            {overview.totalCostNext3Months != null ? (
              <TotalLine>
                Total dos próximos {overview.months.length} meses:
                <strong>
                  R$ {format.money(overview.totalCostNext3Months)}
                </strong>
              </TotalLine>
            ) : (
              <Note>
                Alguns meses ficaram sem tarifa ANEEL (PCAT) vigente para o
                cálculo do gasto — o total do período não é exibido para não
                subestimar o valor.
              </Note>
            )}
          </>
        )}
      </Card>
    </Container>
  );
};
